import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Get token from localStorage
const getToken = () => localStorage.getItem('token');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add token to requests automatically
api.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Handle token expiration and authentication errors
api.interceptors.response.use(
    (response) => {
        // If response is successful, just return it
        return response;
    },
    (error) => {
        // If token expired or unauthorized (401)
        if (error.response?.status === 401) {
            // Clear all auth data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('organization');
            
            // Redirect to login
            window.location.href = '/login';
            
            // Optional: Show user-friendly message
            setTimeout(() => {
                alert('Your session has expired. Please login again.');
            }, 100);
        }
        
        return Promise.reject(error);
    }
);

// Auth API calls
export const authAPI = {
    register: (userData) => api.post('/auth/register', userData),
    login: (credentials) => api.post('/auth/login', credentials),
    getCurrentUser: () => api.get('/auth/me')
};

// Knowledge API calls
export const knowledgeAPI = {
    getAll: () => api.get('/knowledge'),
    getOne: (id) => api.get(`/knowledge/${id}`),
    create: (data) => api.post('/knowledge', data),
    update: (id, data) => api.put(`/knowledge/${id}`, data),
    delete: (id) => api.delete(`/knowledge/${id}`),
    getNotionContent: (id) => api.get(`/knowledge/${id}/notion-content`),
    getConfluenceContent: (id) => api.get(`/knowledge/${id}/confluence-content`),
    toggleQuiz: (id) => api.patch(`/knowledge/${id}/toggle-quiz`)
};

// Completions API calls
export const completionsAPI = {
    getAssignments: () => api.get('/completions/assignments'),
    getCompleted: () => api.get('/completions/completed'),
    markComplete: (data) => api.post('/completions', data),
    getSummary: () => api.get('/completions/summary'),
    getFlashcards: () => api.get('/completions/flashcards')
};

// Comments API calls
export const commentsAPI = {
    getByKnowledge: (knowledgeId) => api.get(`/comments/${knowledgeId}`),
    create: (data) => api.post('/comments', data),
    delete: (id) => api.delete(`/comments/${id}`)
};

// Notion API calls
export const notionAPI = {
    getConfig: () => api.get('/notion/config'),
    saveConfig: (data) => api.post('/notion/config', data),
    deleteConfig: () => api.delete('/notion/config'),
    sync: () => api.post('/notion/sync'),
    getSyncLogs: (limit = 20) => api.get(`/notion/sync-logs?limit=${limit}`),
    getSyncErrors: (syncLogId) => api.get(`/notion/sync-logs/${syncLogId}/errors`),
    getStatus: () => api.get('/notion/status')
};

// Confluence API calls
export const confluenceAPI = {
    // OAuth
    getAuthUrl: () => api.get('/confluence/oauth/authorize'),

    // Config
    getConfig: () => api.get('/confluence/config'),
    updateConfig: (data) => api.post('/confluence/config', data),
    deleteConfig: () => api.delete('/confluence/config'),

    // Sync
    sync: () => api.post('/confluence/sync', { syncType: 'incremental' }),
    getSyncLogs: (limit = 20) => api.get(`/confluence/sync-logs?limit=${limit}`),
    getSyncErrors: (syncLogId) => api.get(`/confluence/sync-logs/${syncLogId}/errors`),
    getStatus: () => api.get('/confluence/status'),

    // Spaces
    getSpaces: () => api.get('/confluence/spaces')
};

// Stripe/Billing API calls
export const billingAPI = {
    createCheckoutSession: (data) => api.post('/stripe/create-checkout-session', data),
    createPortalSession: () => api.post('/stripe/create-portal-session'),
    getSubscription: () => api.get('/stripe/subscription')
};

// Daily Questions API calls
export const dailyQuestionsAPI = {
    getToday: () => api.get('/daily-questions/today'),
    submitAnswer: (data) => api.post('/daily-questions/answer', data),
    getStreak: () => api.get('/daily-questions/streak'),
    getLeaderboard: (limit = 5) => api.get(`/daily-questions/leaderboard?limit=${limit}`),
    generate: () => api.post('/daily-questions/generate')
};

export default api;